import { useEffect, useState } from "react";
import { useParams } from "react-router";
import Chart from "react-apexcharts";
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  Hammer,
  BarChart3,
  PiggyBank,
} from "lucide-react";
import { apiFetch } from "@/service/api";

export default function PresupuestoProyecto() {
  const { id: proyecto_id } = useParams();
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    apiFetch(`movimientos/presupuesto/${proyecto_id}`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => {});
  }, [proyecto_id]);

  if (!data)
    return (
      <div className="p-10 text-center animate-pulse text-gray-500">
        Cargando dashboard financiero...
      </div>
    );

  const presupuesto = Number(data.presupuesto || 0);
  const costoActividades = Number(data.totalCostoActividades || 0);
  const ingresos = Number(data.ingresos || 0);
  const gastos = Number(data.gastos || 0);
  const balance = Number(data.balance || 0);
  const restanteFijo = Number(data.restanteFijo || 0);
  const restanteDinamico = Number(data.restanteDinamico || 0);
  const usado = costoActividades + gastos;
  const porcentajeUsado =
    presupuesto > 0 ? Math.min(100, (usado / presupuesto) * 100) : 0;
  const porcentajeIngresos =
    ingresos + presupuesto > 0
      ? (ingresos / (presupuesto + ingresos)) * 100
      : 0;

  const donutOptions: any = {
    chart: { type: "donut", fontFamily: "Inter, sans-serif" },
    labels: ["Costo Actividades", "Gastos", "Ingresos"],
    colors: ["#F59E0B", "#EF4444", "#10B981"],
    legend: { position: "bottom" },
    dataLabels: { enabled: false },
    plotOptions: { pie: { donut: { size: "65%" } } },
    tooltip: { y: { formatter: (val: number) => `$${val.toLocaleString()}` } },
  };
  const donutSeries = [costoActividades, gastos, ingresos].map((v) =>
    Number(v),
  );

  const barOptions: any = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      fontFamily: "Inter, sans-serif",
    },
    colors: ["#3B82F6", "#8B5CF6", "#10B981", "#F59E0B"],
    plotOptions: {
      bar: { borderRadius: 8, columnWidth: "45%", distributed: true },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: [
        "Presupuesto Inicial",
        "Restante Fijo",
        "Balance Final",
        "Disponible Dinámico",
      ],
      labels: { style: { fontSize: "11px" } },
    },
    yaxis: {
      labels: { formatter: (val: number) => `$${(val / 1000000).toFixed(1)}M` },
    },
    grid: { borderColor: "#E5E7EB" },
    tooltip: {
      y: { formatter: (val: number) => `$${Number(val).toLocaleString()}` },
    },
    legend: { show: false },
  };
  const barSeries = [
    {
      name: "Monto",
      data: [presupuesto, restanteFijo, balance, restanteDinamico],
    },
  ];

  const radialOptions: any = {
    chart: { type: "radialBar", sparkline: { enabled: true } },
    colors: [balance >= 0 ? "#10B981" : "#EF4444"],
    plotOptions: {
      radialBar: {
        hollow: { size: "65%" },
        dataLabels: {
          name: { show: false },
          value: {
            fontSize: "18px",
            fontWeight: 700,
            formatter: (val: number) => `${val.toFixed(1)}%`,
          },
        },
      },
    },
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="rounded-2xl border bg-white p-5 dark:bg-white/[0.03] dark:border-gray-800">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-gray-500">Presupuesto</span>
            <div className="p-2 bg-blue-50 rounded-lg">
              <Wallet className="w-4 h-4 text-blue-600" />
            </div>
          </div>
          <p className="text-xl font-bold text-gray-800 dark:text-white">
            ${presupuesto.toLocaleString()}
          </p>
          <p className="text- text-gray-400 mt-1">
            {data.actividades || 0} actividades
          </p>
        </div>
        <div className="rounded-2xl border bg-white p-5 dark:bg-white/[0.03] dark:border-gray-800">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-gray-500">Costo Actividades</span>
            <div className="p-2 bg-amber-50 rounded-lg">
              <Hammer className="w-4 h-4 text-amber-600" />
            </div>
          </div>
          <p className="text-xl font-bold text-amber-600">
            ${costoActividades.toLocaleString()}
          </p>
          <div className="w-full bg-gray-100 h-1.5 rounded-full mt-3">
            <div
              className="bg-amber-500 h-1.5 rounded-full"
              style={{
                width: `${Math.min(
                  100,
                  (costoActividades / (presupuesto || 1)) * 100,
                )}%`,
              }}
            />
          </div>
        </div>
        <div className="rounded-2xl border bg-white p-5 dark:bg-white/[0.03] dark:border-gray-800">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-gray-500">Ingresos</span>
            <div className="p-2 bg-green-50 rounded-lg">
              <TrendingUp className="w-4 h-4 text-green-600" />
            </div>
          </div>
          <p className="text-xl font-bold text-green-600">
            +${ingresos.toLocaleString()}
          </p>
          <p className="text- text-green-600 mt-1">
            +{porcentajeIngresos.toFixed(1)}% sobre base
          </p>
        </div>
        <div className="rounded-2xl border bg-white p-5 dark:bg-white/[0.03] dark:border-gray-800">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-gray-500">Gastos</span>
            <div className="p-2 bg-red-50 rounded-lg">
              <TrendingDown className="w-4 h-4 text-red-600" />
            </div>
          </div>
          <p className="text-xl font-bold text-red-600">
            -${gastos.toLocaleString()}
          </p>
          <div className="w-full bg-gray-100 h-1.5 rounded-full mt-3">
            <div
              className="bg-red-500 h-1.5 rounded-full"
              style={{
                width: `${Math.min(100, (gastos / (presupuesto || 1)) * 100)}%`,
              }}
            />
          </div>
        </div>
        <div
          className={`rounded-2xl border p-5 ${
            balance >= 0
              ? "bg-green-50/50 border-green-200"
              : "bg-red-50/50 border-red-200"
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-gray-600 font-medium">
              Balance Final
            </span>
            <div
              className={`p-2 rounded-lg ${
                balance >= 0 ? "bg-green-100" : "bg-red-100"
              }`}
            >
              <PiggyBank
                className={`w-4 h-4 ${
                  balance >= 0 ? "text-green-700" : "text-red-700"
                }`}
              />
            </div>
          </div>
          <p
            className={`text-xl font-bold ${
              balance >= 0 ? "text-green-700" : "text-red-700"
            }`}
          >
            ${balance.toLocaleString()}
          </p>
          <p className="text- mt-1 text-gray-500">
            {balance >= 0 ? "Proyecto rentable" : "Sobrecosto"}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="rounded-2xl border bg-white p-6 dark:bg-white/[0.03] dark:border-gray-800">
          <h4 className="font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Distribución de Flujo
          </h4>
          <Chart
            options={donutOptions}
            series={donutSeries}
            type="donut"
            height={300}
          />
          <div className="mt-4 space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">
                Total usado (Actividades+Gastos)
              </span>
              <b>${usado.toLocaleString()}</b>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Disponible dinámico</span>
              <b className="text-blue-600">
                ${restanteDinamico.toLocaleString()}
              </b>
            </div>
          </div>
        </div>
        <div className="lg:col-span-2 rounded-2xl border bg-white p-6 dark:bg-white/[0.03] dark:border-gray-800">
          <h4 className="font-semibold text-gray-800 dark:text-white mb-4">
            Evolución Presupuestal
          </h4>
          <Chart
            options={barOptions}
            series={barSeries}
            type="bar"
            height={320}
          />
          <div className="grid grid-cols-3 gap-3 mt-2">
            <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-xl text-center">
              <p className="text- text-gray-500">Restante Fijo</p>
              <p className="font-bold text-sm">
                ${restanteFijo.toLocaleString()}
              </p>
              <p className="text- text-gray-400">Presupuesto - Actividades</p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-xl text-center border border-blue-100">
              <p className="text- text-blue-600">Disponible Real</p>
              <p className="font-bold text-sm text-blue-700">
                ${restanteDinamico.toLocaleString()}
              </p>
              <p className="text- text-blue-400">Con ingresos y gastos</p>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-xl text-center">
              <p className="text- text-amber-600">% Consumido</p>
              <p className="font-bold text-sm">{porcentajeUsado.toFixed(1)}%</p>
              <div className="w-full bg-amber-200 h-1 rounded-full mt-1">
                <div
                  className="bg-amber-600 h-1 rounded-full"
                  style={{ width: `${porcentajeUsado}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 rounded-2xl border bg-white p-6 dark:bg-white/[0.03] dark:border-gray-800">
          <h4 className="font-semibold mb-3 text-gray-800 dark:text-white">
            Cálculo aplicado (Punto 1 - Balance Dinámico)
          </h4>
          <div className="bg-gray-900 text-gray-100 p-4 rounded-xl font-mono text-sm overflow-x-auto">
            <span className="text-gray-400">// Fórmula:</span>
            <br />
            Balance = Presupuesto + Ingresos - Gastos - CostoActividades
            <br />
            <span className="text-blue-400">
              {balance.toLocaleString()}
            </span> = {presupuesto.toLocaleString()} +{" "}
            {ingresos.toLocaleString()} - {gastos.toLocaleString()} -{" "}
            {costoActividades.toLocaleString()}
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between bg-gray-50 p-3 rounded-lg">
              <span>Presupuesto inicial</span>
              <b>${presupuesto.toLocaleString()}</b>
            </div>
            <div className="flex justify-between bg-green-50 p-3 rounded-lg text-green-700">
              <span>+ Ingresos extra</span>
              <b>+${ingresos.toLocaleString()}</b>
            </div>
            <div className="flex justify-between bg-red-50 p-3 rounded-lg text-red-700">
              <span>- Gastos</span>
              <b>-${gastos.toLocaleString()}</b>
            </div>
            <div className="flex justify-between bg-amber-50 p-3 rounded-lg text-amber-700">
              <span>- Costo Actividades</span>
              <b>-${costoActividades.toLocaleString()}</b>
            </div>
          </div>
        </div>
        <div className="rounded-2xl border bg-white p-6 text-center dark:bg-white/[0.03] dark:border-gray-800 flex flex-col items-center justify-center">
          <h4 className="font-semibold text-sm mb-2">Salud Financiera</h4>
          <Chart
            options={radialOptions}
            series={[
              balance >= 0
                ? Math.min(100, (balance / presupuesto) * 100 + 50)
                : Math.max(0, 50 + (balance / presupuesto) * 100),
            ]}
            type="radialBar"
            height={220}
          />
          <p
            className={`mt-2 font-bold ${
              balance >= 0 ? "text-green-600" : "text-red-600"
            }`}
          >
            {balance >= 0 ? "POSITIVO" : "EN DÉFICIT"}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {balance >= 0
              ? `Te quedan $${restanteDinamico.toLocaleString()} para operar`
              : `Faltan $${Math.abs(restanteDinamico).toLocaleString()}`}
          </p>
        </div>
      </div>
    </div>
  );
}
